import React, { useState } from 'react';
import { ChevronLeft, Smartphone, QrCode, KeyRound, ShieldCheck, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { DemoFillBanner } from '../ui/DemoFillBanner';
import { SAMPLE_ACCOUNT_SECURITY } from '../../constants/sampleData';
type SecurityMethod = 'secure2u' | 'authenticator' | 'hardware_token' | null;
interface AccountSecurityJourneyProps {
  onComplete: (data: {
    method: Exclude<SecurityMethod, null>;
  }) => void;
  onExit: () => void;
  initialData?: {
    method?: Exclude<SecurityMethod, null>;
  };
}
interface MethodOption {
  id: Exclude<SecurityMethod, null>;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  recommended?: boolean;
  primaryCta?: boolean;
}
const METHODS: MethodOption[] = [{
  id: 'secure2u',
  icon: Smartphone,
  title: 'Maybank Secure2u',
  subtitle: 'Approve logins and payout changes directly from your MAE or Maybank2u Biz app. Meets all BNM security mandates.',
  recommended: true,
  primaryCta: true
}, {
  id: 'authenticator',
  icon: QrCode,
  title: 'Authenticator App',
  subtitle: 'Use a secure 3rd-party app like Google Authenticator. Ideal for agencies or businesses managing multiple portals.'
}, {
  id: 'hardware_token',
  icon: KeyRound,
  title: 'Hardware Security Token',
  subtitle: 'Link your existing Maybank corporate hardware token for offline code generation.'
}];
export function AccountSecurityJourney({
  onComplete,
  onExit,
  initialData
}: AccountSecurityJourneyProps) {
  const [selected, setSelected] = useState<SecurityMethod>(initialData?.method ?? null);
  const handleSubmit = () => {
    if (!selected) return;
    onComplete({
      method: selected
    });
  };
  return <div className="flex flex-col h-full max-w-xl mx-auto pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-69">
      {/* Back link */}
      <button type="button" onClick={onExit} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6 self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded" data-id="element-70">
        <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-71" />
        Back to overview
      </button>

      {/* Header */}
      <header className="mb-8" data-id="element-72">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#FFCC00]/15 mb-5" data-id="element-73">
          <ShieldCheck className="w-6 h-6 text-gray-900" aria-hidden="true" data-id="element-74" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-75">
          Keep your account secure
        </h1>
        <p className="text-base text-gray-600 leading-relaxed" data-id="element-76">
          To comply with Bank Negara Malaysia (BNM) regulations, please set up a
          secure authentication method to protect your account.
        </p>
      </header>

      <DemoFillBanner className="mb-6" message="Demo mode — auto-select Maybank Secure2u." onFill={() => setSelected(SAMPLE_ACCOUNT_SECURITY.method)} data-id="element-77" />

      {/* Selection cards */}
      <div role="radiogroup" aria-label="Authentication method" className="space-y-4" data-id="element-78">
        {METHODS.map(method => <MethodCard key={method.id} method={method} selected={selected === method.id} onSelect={() => setSelected(method.id)} data-id="element-79" />)}
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 left-0 right-0 -mx-2 mt-12 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-10" data-id="element-80">
        <div className="flex items-center justify-between gap-3 py-4 px-2" data-id="element-81">
          <Button variant="outline" type="button" onClick={onExit} data-id="element-82">
            Back
          </Button>
          <Button onClick={handleSubmit} disabled={!selected} data-id="element-83">
            Save & Continue
          </Button>
        </div>
      </div>
    </div>;
}
// ---------- Sub-component ----------
function MethodCard({
  method,
  selected,
  onSelect
}: {
  method: MethodOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = method.icon;
  return <div className={`group relative rounded-xl border-2 bg-white transition-all
        ${selected ? 'border-[#FFCC00] shadow-md ring-4 ring-[#FFCC00]/20' : 'border-gray-300 hover:border-gray-500 hover:shadow-sm'}`} data-id="element-84">
      {method.recommended && <span className="absolute -top-2.5 left-5 inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-gray-900 bg-[#FFCC00] border-2 border-white" data-id="element-85">
          Recommended
        </span>}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5" data-id="element-86">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
            ${selected ? 'bg-[#FFCC00] text-gray-900' : 'bg-gray-100 text-gray-700 group-hover:bg-gray-200'}`} data-id="element-87">
          <Icon className="w-6 h-6" aria-hidden="true" data-id="element-88" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-89">
          <h3 className="font-semibold text-base text-gray-900" data-id="element-90">
            {method.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-91">
            {method.subtitle}
          </p>
        </div>

        {/* Action */}
        <div className="flex-shrink-0 sm:self-center" data-id="element-92">
          {selected ? <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm font-semibold" role="status" data-id="element-93">
              <Check className="w-4 h-4" aria-hidden="true" data-id="element-94" />
              Selected
            </span> : <button type="button" role="radio" aria-checked={false} aria-label={`Set up ${method.title}`} onClick={onSelect} className={`inline-flex items-center justify-center min-h-[44px] px-5 rounded-lg font-semibold text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                ${method.primaryCta ? 'bg-[#FFCC00] text-black hover:bg-[#E6B800] focus-visible:ring-black' : 'border-2 border-gray-700 bg-white text-gray-900 hover:bg-gray-50 focus-visible:ring-black'}`} data-id="element-95">
              Set up
            </button>}
        </div>
      </div>
    </div>;
}